import React, { useState, useEffect } from 'react';
import {
  Cloud, ShieldCheck, CheckCircle2, AlertCircle, RefreshCw,
  Edit, Check, Key, X, Lock, ChevronDown, ChevronRight,
  HardDrive, Server, FileText, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSupportSettingsStore, AwsS3StorageSettings } from '../../../store/supportSettingsStore';
import { cn } from '../../../lib/utils';

export const AWS_S3_REGIONS = [
  { id: 'ap-south-1', name: 'Asia Pacific (Mumbai) [ap-south-1]' },
  { id: 'ap-south-2', name: 'Asia Pacific (Hyderabad) [ap-south-2]' },
  { id: 'us-east-1', name: 'US East (N. Virginia) [us-east-1]' },
  { id: 'us-east-2', name: 'US East (Ohio) [us-east-2]' },
  { id: 'us-west-1', name: 'US West (N. California) [us-west-1]' },
  { id: 'us-west-2', name: 'US West (Oregon) [us-west-2]' },
  { id: 'eu-west-1', name: 'Europe (Ireland) [eu-west-1]' },
  { id: 'eu-west-2', name: 'Europe (London) [eu-west-2]' },
  { id: 'eu-west-3', name: 'Europe (Paris) [eu-west-3]' },
  { id: 'eu-central-1', name: 'Europe (Frankfurt) [eu-central-1]' },
  { id: 'eu-north-1', name: 'Europe (Stockholm) [eu-north-1]' },
  { id: 'ap-southeast-1', name: 'Asia Pacific (Singapore) [ap-southeast-1]' },
  { id: 'ap-southeast-2', name: 'Asia Pacific (Sydney) [ap-southeast-2]' },
  { id: 'ap-northeast-1', name: 'Asia Pacific (Tokyo) [ap-northeast-1]' },
  { id: 'ca-central-1', name: 'Canada (Central) [ca-central-1]' },
  { id: 'sa-east-1', name: 'South America (São Paulo) [sa-east-1]' },
  { id: 'me-south-1', name: 'Middle East (Bahrain) [me-south-1]' },
  { id: 'af-south-1', name: 'Africa (Cape Town) [af-south-1]' },
];

const DEFAULT_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'application/json',
  'application/zip',
];

export function CloudStorageTab() {
  const {
    s3Settings,
    isLoadingS3Settings,
    isSavingS3Settings,
    isTestingS3Connection,
    s3TestResult,
    fetchS3Settings,
    updateS3Settings,
    testS3Connection,
  } = useSupportSettingsStore();

  const [isEditing, setIsEditing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Form state
  const [formState, setFormState] = useState<AwsS3StorageSettings>({
    provider: 'aws_s3',
    region: 'ap-south-1',
    bucket_name: '',
    key_prefix: 'support/',
    endpoint_url: '',
    force_path_style: false,
    aws_access_key_id: '',
    aws_secret_access_key_set: false,
    use_iam_role: false,
    sse_mode: 'AES256',
    kms_key_id: '',
    presign_upload_expiry_seconds: 300,
    presign_download_expiry_seconds: 300,
    max_file_size_mb: 25,
    allowed_mime_types: DEFAULT_MIME_TYPES,
    mirror_ticket_json: true,
  });

  // Secret key replacement state
  const [replaceSecretKey, setReplaceSecretKey] = useState(false);
  const [newSecretKey, setNewSecretKey] = useState('');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Confirm dialog for Bucket or Region change
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState<any>(null);

  useEffect(() => {
    fetchS3Settings();
  }, [fetchS3Settings]);

  useEffect(() => {
    if (s3Settings) {
      setFormState(s3Settings);
    }
  }, [s3Settings]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    const b = formState.bucket_name.trim();
    if (!b) {
      errs.bucket_name = 'Bucket name is required.';
    } else {
      if (b.length < 3 || b.length > 63) {
        errs.bucket_name = 'Bucket name must be between 3 and 63 characters long.';
      } else if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(b)) {
        errs.bucket_name = 'Bucket name must start/end with a letter or digit and contain only lowercase letters, numbers, hyphens, and periods.';
      } else if (b.includes('..')) {
        errs.bucket_name = 'Bucket name must not contain two adjacent periods.';
      } else if (/^(\d{1,3}\.){3}\d{1,3}$/.test(b)) {
        errs.bucket_name = 'Bucket name must not resemble an IP address.';
      }
    }

    if (!formState.region) {
      errs.region = 'AWS region is required.';
    }

    if (!formState.use_iam_role) {
      if (!formState.aws_access_key_id || !formState.aws_access_key_id.trim()) {
        errs.aws_access_key_id = 'Access Key ID is required when IAM Role is disabled.';
      }
      if (!formState.aws_secret_access_key_set && (!newSecretKey || !newSecretKey.trim())) {
        errs.aws_secret_access_key = 'Secret Access Key is required.';
      }
    }

    if (formState.sse_mode === 'aws:kms') {
      if (!formState.kms_key_id || !formState.kms_key_id.trim()) {
        errs.kms_key_id = 'KMS Key ID or ARN is required when KMS encryption is selected.';
      }
    }

    const upExp = Number(formState.presign_upload_expiry_seconds);
    if (isNaN(upExp) || upExp < 60 || upExp > 3600) {
      errs.presign_upload_expiry_seconds = 'Upload expiry must be between 60 and 3600 seconds.';
    }

    const dlExp = Number(formState.presign_download_expiry_seconds);
    if (isNaN(dlExp) || dlExp < 60 || dlExp > 3600) {
      errs.presign_download_expiry_seconds = 'Download expiry must be between 60 and 3600 seconds.';
    }

    const size = Number(formState.max_file_size_mb);
    if (isNaN(size) || size < 1 || size > 500) {
      errs.max_file_size_mb = 'Max file size must be between 1 and 500 MB.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const executeSave = async (payload: any) => {
    const res = await updateS3Settings(payload);
    if (res.success) {
      setIsEditing(false);
      setReplaceSecretKey(false);
      setNewSecretKey('');
      setErrors({});
      setShowConfirmModal(false);
      setPendingSavePayload(null);
    }
  };

  const handleSaveClick = async () => {
    if (!validate()) {
      toast.error('Please fix validation errors before saving.');
      return;
    }

    let prefix = formState.key_prefix.trim().replace(/^\/+/, '');
    if (!prefix.endsWith('/')) prefix += '/';

    const payload: any = {
      ...formState,
      key_prefix: prefix,
      presign_upload_expiry_seconds: Number(formState.presign_upload_expiry_seconds),
      presign_download_expiry_seconds: Number(formState.presign_download_expiry_seconds),
      max_file_size_mb: Number(formState.max_file_size_mb),
    };

    if (replaceSecretKey && newSecretKey.trim()) {
      payload.aws_secret_access_key = newSecretKey.trim();
    }

    // Check if bucket or region changed from originally saved settings
    const bucketChanged = s3Settings && s3Settings.bucket_name && s3Settings.bucket_name !== formState.bucket_name;
    const regionChanged = s3Settings && s3Settings.region && s3Settings.region !== formState.region;

    if (bucketChanged || regionChanged) {
      setPendingSavePayload(payload);
      setShowConfirmModal(true);
      return;
    }

    await executeSave(payload);
  };

  const handleCancel = () => {
    if (s3Settings) {
      setFormState(s3Settings);
    }
    setIsEditing(false);
    setReplaceSecretKey(false);
    setNewSecretKey('');
    setErrors({});
  };

  const handleTestConnection = async () => {
    const testPayload: any = {
      bucket_name: formState.bucket_name,
      region: formState.region,
      key_prefix: formState.key_prefix,
      use_iam_role: formState.use_iam_role,
      endpoint_url: formState.endpoint_url,
      force_path_style: formState.force_path_style,
      sse_mode: formState.sse_mode,
      kms_key_id: formState.kms_key_id,
    };

    if (!formState.use_iam_role) {
      testPayload.aws_access_key_id = formState.aws_access_key_id;
      if (replaceSecretKey && newSecretKey.trim()) {
        testPayload.aws_secret_access_key = newSecretKey.trim();
      }
    }

    await testS3Connection(testPayload);
  };

  const handleSyncSettings = async () => {
    await fetchS3Settings();
    toast.success('Storage settings reloaded from database.');
  };

  return (
    <div className="space-y-6 text-foreground font-sans">
      {/* 1. TOP HEADER & ACTIONS */}
      <div className="rounded-lg border border-border bg-card p-5 shadow-xs">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border pb-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Cloud className="size-5" />
              </div>
              <div>
                <h2 className="font-display text-section-title font-semibold text-foreground">
                  Cloud Storage
                </h2>
                <p className="text-muted-foreground text-xs">
                  Configure AWS S3 Cloud Vault for ticket attachments and media archives.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-end">
            {/* Sync Settings Button */}
            <button
              type="button"
              disabled={isLoadingS3Settings}
              onClick={handleSyncSettings}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={cn('size-3.5', isLoadingS3Settings && 'animate-spin text-primary')} />
              Sync Settings
            </button>

            {/* Test Connection Button */}
            <button
              type="button"
              disabled={isTestingS3Connection}
              onClick={handleTestConnection}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={cn('size-3.5', isTestingS3Connection && 'animate-spin text-primary')} />
              {isTestingS3Connection ? 'Testing S3 Vault...' : 'Test Connection'}
            </button>

            {/* Read-Only vs Edit Toggle */}
            {!isEditing ? (
              <>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground border border-border text-xs font-semibold">
                  <Lock className="size-3" /> Read-only
                </span>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors cursor-pointer"
                >
                  <Edit className="size-3.5" /> Edit Storage
                </button>
              </>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-warning/10 text-warning-strong border border-warning/20 text-xs font-semibold">
                  <Edit className="size-3" /> Editing
                </span>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
                >
                  <X className="size-3.5" /> Cancel
                </button>
                <button
                  type="button"
                  disabled={isSavingS3Settings}
                  onClick={handleSaveClick}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <Check className="size-3.5" /> {isSavingS3Settings ? 'Saving...' : 'Save Settings'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Multi-Step Test Result Banner */}
        {s3TestResult && (
          <div
            className={cn(
              'mt-4 p-4 rounded-lg border text-xs space-y-3',
              s3TestResult.success
                ? 'bg-success/10 border-success/30 text-success-strong'
                : 'bg-destructive/10 border-destructive/30 text-destructive'
            )}
          >
            <div className="flex items-center justify-between gap-3 font-semibold">
              <div className="flex items-center gap-2">
                {s3TestResult.success ? (
                  <CheckCircle2 className="size-4 shrink-0 text-success" />
                ) : (
                  <AlertCircle className="size-4 shrink-0 text-destructive" />
                )}
                <span>{s3TestResult.message}</span>
              </div>
              <button
                type="button"
                onClick={() => testS3Connection(null)}
                className="text-muted-foreground hover:text-foreground cursor-pointer text-xs underline"
              >
                Dismiss
              </button>
            </div>

            {s3TestResult.steps && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-border/40">
                <div className="flex items-center gap-1.5">
                  <span className={cn('size-2 rounded-full', s3TestResult.steps.reachable?.ok ? 'bg-success' : 'bg-destructive')} />
                  <span>Reachable (HeadBucket): {s3TestResult.steps.reachable?.ok ? 'OK' : 'Failed'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={cn('size-2 rounded-full', s3TestResult.steps.write?.ok ? 'bg-success' : 'bg-destructive')} />
                  <span>Write (PutObject): {s3TestResult.steps.write?.ok ? 'OK' : 'Failed'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={cn('size-2 rounded-full', s3TestResult.steps.read?.ok ? 'bg-success' : 'bg-destructive')} />
                  <span>Read (GetObject): {s3TestResult.steps.read?.ok ? 'OK' : 'Failed'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={cn('size-2 rounded-full', s3TestResult.steps.delete?.ok ? 'bg-success' : 'bg-destructive')} />
                  <span>Delete (Clean-up): {s3TestResult.steps.delete?.ok ? 'OK' : 'Failed'}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 2. STATIC AWS S3 CLOUD VAULT HEADER CARD */}
        <div className="mt-5 rounded-lg border border-primary/20 bg-primary/5 p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-primary text-primary-foreground shadow-xs">
              <Cloud className="size-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display text-card-title font-semibold text-foreground">
                  AWS S3 Cloud Vault
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                  Active Provider
                </span>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground border border-border">
                  Single Cloud Vault Standard
                </span>
              </div>
              <p className="text-muted-foreground text-xs mt-0.5">
                Direct browser presigned uploads to AWS S3 or S3-compatible object storage vaults with server-side encryption at rest.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
            <ShieldCheck className="size-4 text-success" />
            <span>AES-256-GCM Encrypted at Rest</span>
          </div>
        </div>

        {/* 3. CORE S3 CONFIGURATION FORM */}
        <div className="mt-6 space-y-6">
          {/* Row 1: Bucket & Region */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Bucket Name */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">S3 Bucket Name *</label>
              <input
                type="text"
                disabled={!isEditing}
                value={formState.bucket_name}
                onChange={(e) => {
                  setFormState((prev) => ({ ...prev, bucket_name: e.target.value.toLowerCase() }));
                  if (errors.bucket_name) setErrors((prev) => ({ ...prev, bucket_name: '' }));
                }}
                placeholder="e.g. teamone-support-attachments"
                className={cn(
                  'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                  !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              />
              {errors.bucket_name && <p className="text-destructive text-xs">{errors.bucket_name}</p>}
              <p className="text-muted-foreground text-xs">
                S3 bucket conforming to AWS naming standards (lowercase, no spaces, 3-63 chars).
              </p>
            </div>

            {/* AWS Region */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">AWS Region *</label>
              <select
                disabled={!isEditing}
                value={formState.region}
                onChange={(e) => {
                  setFormState((prev) => ({ ...prev, region: e.target.value }));
                  if (errors.region) setErrors((prev) => ({ ...prev, region: '' }));
                }}
                className={cn(
                  'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                  !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              >
                {AWS_S3_REGIONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              {errors.region && <p className="text-destructive text-xs">{errors.region}</p>}
              <p className="text-muted-foreground text-xs">
                Physical AWS data center region hosting the S3 bucket.
              </p>
            </div>
          </div>

          {/* Row 2: Prefix & IAM Role Toggle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Key Prefix */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Key Prefix *</label>
              <input
                type="text"
                disabled={!isEditing}
                value={formState.key_prefix}
                onChange={(e) => setFormState((prev) => ({ ...prev, key_prefix: e.target.value }))}
                placeholder="support/"
                className={cn(
                  'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                  !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              />
              <p className="text-muted-foreground text-xs">
                All objects will be stored under this prefix (e.g. <code>support/</code>).
              </p>
            </div>

            {/* Use IAM Role Toggle */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Authentication Method</label>
              <div className="h-9 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-medium text-foreground">
                  <input
                    type="checkbox"
                    disabled={!isEditing}
                    checked={formState.use_iam_role}
                    onChange={(e) => setFormState((prev) => ({ ...prev, use_iam_role: e.target.checked }))}
                    className="size-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Use EC2/ECS/EKS Default IAM Role Credentials</span>
                </label>
              </div>
              <p className="text-muted-foreground text-xs">
                When enabled, AWS credentials will be automatically resolved from the EC2 instance profile or ECS task role.
              </p>
            </div>
          </div>

          {/* Row 3: IAM Access Keys (Hidden if Use IAM Role is checked) */}
          {!formState.use_iam_role && (
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-2">
                <div className="flex items-center gap-2">
                  <Key className="size-4 text-primary" />
                  <span className="font-display text-card-title font-semibold text-foreground">
                    AWS IAM Credentials
                  </span>
                </div>
                <span className="text-muted-foreground text-xs">
                  Least-privilege S3 credentials
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Access Key ID */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">AWS Access Key ID *</label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    value={formState.aws_access_key_id || ''}
                    onChange={(e) => {
                      setFormState((prev) => ({ ...prev, aws_access_key_id: e.target.value }));
                      if (errors.aws_access_key_id) setErrors((prev) => ({ ...prev, aws_access_key_id: '' }));
                    }}
                    placeholder="AKIA..."
                    className={cn(
                      'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                      !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                    )}
                  />
                  {errors.aws_access_key_id && <p className="text-destructive text-xs">{errors.aws_access_key_id}</p>}
                </div>

                {/* Secret Access Key */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">AWS Secret Access Key *</label>
                  {formState.aws_secret_access_key_set && !replaceSecretKey ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        disabled
                        value="••••••••••••••••"
                        className="w-full h-9 rounded-lg border border-border bg-muted px-3 text-xs text-muted-foreground font-mono cursor-not-allowed"
                      />
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => setReplaceSecretKey(true)}
                          className="h-9 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors shrink-0 cursor-pointer"
                        >
                          Change
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="password"
                          disabled={!isEditing}
                          value={newSecretKey}
                          onChange={(e) => {
                            setNewSecretKey(e.target.value);
                            if (errors.aws_secret_access_key) setErrors((prev) => ({ ...prev, aws_secret_access_key: '' }));
                          }}
                          placeholder="Enter new AWS Secret Access Key"
                          className={cn(
                            'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                            !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                          )}
                        />
                        {formState.aws_secret_access_key_set && isEditing && (
                          <button
                            type="button"
                            onClick={() => {
                              setReplaceSecretKey(false);
                              setNewSecretKey('');
                            }}
                            className="h-9 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors shrink-0 cursor-pointer"
                          >
                            Keep Existing
                          </button>
                        )}
                      </div>
                      {errors.aws_secret_access_key && (
                        <p className="text-destructive text-xs">{errors.aws_secret_access_key}</p>
                      )}
                    </div>
                  )}
                  <p className="text-muted-foreground text-xs">
                    Encrypted with AES-256-GCM. Secret keys are write-only.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Row 4: Server-Side Encryption */}
          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="font-display text-card-title font-semibold text-foreground">
                Server-Side Encryption (SSE)
              </span>
              <span className="text-muted-foreground text-xs">
                Encryption at rest for objects
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">SSE Mode</label>
                <select
                  disabled={!isEditing}
                  value={formState.sse_mode}
                  onChange={(e) => setFormState((prev) => ({ ...prev, sse_mode: e.target.value as any }))}
                  className={cn(
                    'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                    !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                  )}
                >
                  <option value="AES256">AES256 (Amazon S3-Managed Keys / SSE-S3)</option>
                  <option value="aws:kms">aws:kms (AWS Key Management Service / SSE-KMS)</option>
                </select>
                <p className="text-muted-foreground text-xs">
                  Default AES256 encryption uses AWS S3-managed keys with zero key management cost.
                </p>
              </div>

              {formState.sse_mode === 'aws:kms' && (
                <div className="space-y-1">
                  <label className="text-xs font-medium text-foreground">KMS Key ID or ARN *</label>
                  <input
                    type="text"
                    disabled={!isEditing}
                    value={formState.kms_key_id || ''}
                    onChange={(e) => {
                      setFormState((prev) => ({ ...prev, kms_key_id: e.target.value }));
                      if (errors.kms_key_id) setErrors((prev) => ({ ...prev, kms_key_id: '' }));
                    }}
                    placeholder="arn:aws:kms:ap-south-1:123456789012:key/..."
                    className={cn(
                      'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                      !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                    )}
                  />
                  {errors.kms_key_id && <p className="text-destructive text-xs">{errors.kms_key_id}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Row 5: URL Expiries & File Size Limits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Presigned Upload Expiry (seconds)</label>
              <input
                type="number"
                disabled={!isEditing}
                min={60}
                max={3600}
                value={formState.presign_upload_expiry_seconds}
                onChange={(e) => setFormState((prev) => ({ ...prev, presign_upload_expiry_seconds: Number(e.target.value) }))}
                className={cn(
                  'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                  !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              />
              {errors.presign_upload_expiry_seconds && <p className="text-destructive text-xs">{errors.presign_upload_expiry_seconds}</p>}
              <p className="text-muted-foreground text-xs">Range: 60 - 3600s (Default: 300s)</p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Presigned Download Expiry (seconds)</label>
              <input
                type="number"
                disabled={!isEditing}
                min={60}
                max={3600}
                value={formState.presign_download_expiry_seconds}
                onChange={(e) => setFormState((prev) => ({ ...prev, presign_download_expiry_seconds: Number(e.target.value) }))}
                className={cn(
                  'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                  !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              />
              {errors.presign_download_expiry_seconds && <p className="text-destructive text-xs">{errors.presign_download_expiry_seconds}</p>}
              <p className="text-muted-foreground text-xs">Range: 60 - 3600s (Default: 300s)</p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Max File Size (MB)</label>
              <input
                type="number"
                disabled={!isEditing}
                min={1}
                max={500}
                value={formState.max_file_size_mb}
                onChange={(e) => setFormState((prev) => ({ ...prev, max_file_size_mb: Number(e.target.value) }))}
                className={cn(
                  'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary',
                  !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              />
              {errors.max_file_size_mb && <p className="text-destructive text-xs">{errors.max_file_size_mb}</p>}
              <p className="text-muted-foreground text-xs">Range: 1 - 500 MB (Default: 25 MB)</p>
            </div>
          </div>

          {/* Row 6: Ticket Mirroring & Allowed MIME types */}
          <div className="rounded-lg border border-border bg-card p-4 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="font-display text-card-title font-semibold text-foreground">
                Ticket Sync &amp; Whitelist
              </span>
              <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-medium text-foreground">
                <input
                  type="checkbox"
                  disabled={!isEditing}
                  checked={formState.mirror_ticket_json}
                  onChange={(e) => setFormState((prev) => ({ ...prev, mirror_ticket_json: e.target.checked }))}
                  className="size-4 rounded border-border text-primary focus:ring-primary"
                />
                <span>Mirror Ticket JSON to S3</span>
              </label>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-foreground block">
                Allowed File MIME Types
              </label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-lg border border-border bg-muted/30">
                {formState.allowed_mime_types?.map((mime) => (
                  <span
                    key={mime}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-card border border-border text-foreground font-mono"
                  >
                    {mime}
                  </span>
                ))}
              </div>
              <p className="text-muted-foreground text-xs">
                Restricted to standard images, PDFs, office documents, text, csv, and json payloads.
              </p>
            </div>
          </div>

          {/* Collapsible: Advanced / S3-compatible Settings */}
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/40 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <Server className="size-4 text-muted-foreground" />
                <span className="font-display text-card-title font-semibold text-foreground">
                  Advanced / S3-Compatible Storage Providers (MinIO, Cloudflare R2, Wasabi)
                </span>
              </div>
              {showAdvanced ? <ChevronDown className="size-4 text-muted-foreground" /> : <ChevronRight className="size-4 text-muted-foreground" />}
            </button>

            {showAdvanced && (
              <div className="p-4 pt-0 border-t border-border space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground">Custom Endpoint URL (Optional)</label>
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={formState.endpoint_url || ''}
                      onChange={(e) => setFormState((prev) => ({ ...prev, endpoint_url: e.target.value }))}
                      placeholder="e.g. https://play.min.io or https://<id>.r2.cloudflarestorage.com"
                      className={cn(
                        'w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono focus:outline-none focus:ring-1 focus:ring-primary',
                        !isEditing && 'bg-muted text-muted-foreground cursor-not-allowed'
                      )}
                    />
                    <p className="text-muted-foreground text-xs">
                      Leave empty for official Amazon AWS S3.
                    </p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-foreground">Path Style Addressing</label>
                    <div className="h-9 flex items-center">
                      <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-medium text-foreground">
                        <input
                          type="checkbox"
                          disabled={!isEditing}
                          checked={formState.force_path_style}
                          onChange={(e) => setFormState((prev) => ({ ...prev, force_path_style: e.target.checked }))}
                          className="size-4 rounded border-border text-primary focus:ring-primary"
                        />
                        <span>Force Path Style (e.g. endpoint/bucket rather than bucket.endpoint)</span>
                      </label>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      Required by MinIO and certain on-prem S3 appliances.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CONFIRMATION DIALOG: BUCKET / REGION CHANGE */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="rounded-xl border border-border bg-card p-6 shadow-xl max-w-md w-full space-y-4">
            <div className="flex items-center gap-3 text-warning">
              <div className="p-2 rounded-lg bg-warning/10">
                <AlertTriangle className="size-6 text-warning" />
              </div>
              <h3 className="font-display text-card-title font-semibold text-foreground">
                Confirm Bucket or Region Change
              </h3>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              You are updating the active S3 bucket name or region. <strong className="text-foreground">Existing attachments are not moved automatically to the new bucket.</strong>
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Are you sure you want to proceed and save these settings?
            </p>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => {
                  setShowConfirmModal(false);
                  setPendingSavePayload(null);
                }}
                className="px-3.5 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSavingS3Settings}
                onClick={() => executeSave(pendingSavePayload)}
                className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer"
              >
                {isSavingS3Settings ? 'Updating...' : 'Yes, Update Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
